import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  PaFilterBar,
  PaPageHeader,
} from "components/platform-admin";
import PlatformAdminLayout from "pages/PlatformAdmin/layout/PlatformAdminLayout";
import PaAccessDenied from "pages/PlatformAdmin/layout/PaAccessDenied";
import { platformRoleAccess } from "helpers/RolesPermission";
import { getPaginationObj } from "Utils/utils";
import {
  exportPlatformAiUsageCsv,
  fetchPlatformAiUsageOverview,
  fetchPlatformAiUsageTenants,
  refreshPlatformAiUsage,
} from "store/services/platformAdmin.services";
import {
  URL_PLATFORM_ADMIN_AI_USAGE_OVERVIEW,
  URL_PLATFORM_ADMIN_AI_USAGE_TENANTS,
} from "store/services/URL";
import AiUsageFilterBar from "./AiUsageFilterBar";
import AiUsageKpiStrip from "./AiUsageKpiStrip";
import AiUsageInsightsStrip from "./AiUsageInsightsStrip";
import AiUsageOverviewSkeleton from "./AiUsageOverviewSkeleton";
import AiUsageTenantTable from "./AiUsageTenantTable";
import PlatformAiCostOverTimeChart from "./PlatformAiCostOverTimeChart";
import PlatformAiUsageByFeatureChart from "./PlatformAiUsageByFeatureChart";
import PlatformAiUsageByModelChart from "./PlatformAiUsageByModelChart";
import PlatformAiUsageBySectorChart from "./PlatformAiUsageBySectorChart";
import PlatformAiUsageByStatusChart from "./PlatformAiUsageByStatusChart";
import {
  buildAiUsageOverviewQuery,
  buildAiUsageTenantsQuery,
  createAiUsageFiltersForPreset,
  createInitialAiUsageFilters,
  DEFAULT_AI_USAGE_FILTERS,
  availableAiUsageGranularities,
  formatAiUsageMeta,
  formatAlertSummary,
  normalizeTenantSort,
  resolveAiUsageDateRange,
  resolveAiUsageGranularity,
} from "./aiUsageOverview.utils";

const AiUsageOverview = () => {
  const canViewAiUsage = platformRoleAccess("platform.ai_usage.view");
  const canExportAiUsage = platformRoleAccess("platform.ai_usage.export");
  const canRefreshAiUsage = platformRoleAccess("platform.ai_usage.refresh");
  const [filters, setFilters] = useState(() => createInitialAiUsageFilters());
  const [searchInput, setSearchInput] = useState("");
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [tenantsPayload, setTenantsPayload] = useState(null);
  const [pagination, setPagination] = useState(getPaginationObj());

  const dateRange = useMemo(() => resolveAiUsageDateRange(filters), [filters]);

  const granularity = useMemo(
    () => resolveAiUsageGranularity(filters, dateRange.from, dateRange.to),
    [filters, dateRange.from, dateRange.to]
  );

  const granularityOptions = useMemo(
    () => availableAiUsageGranularities(dateRange.from, dateRange.to),
    [dateRange.from, dateRange.to]
  );

  const loadOverview = useCallback(async () => {
    if (!canViewAiUsage) {
      setOverviewLoading(false);
      return;
    }

    setOverviewLoading(true);
    setError(null);

    try {
      const endpoint = buildAiUsageOverviewQuery(filters, URL_PLATFORM_ADMIN_AI_USAGE_OVERVIEW);
      const data = await fetchPlatformAiUsageOverview(endpoint);
      setOverview(data);
    } catch (err) {
      setOverview(null);
      setError(err?.message || "Failed to load AI usage overview");
    } finally {
      setOverviewLoading(false);
    }
  }, [canViewAiUsage, filters]);

  const loadTenants = useCallback(async () => {
    if (!canViewAiUsage) {
      setTenantsLoading(false);
      return;
    }

    setTenantsLoading(true);

    try {
      const endpoint = buildAiUsageTenantsQuery(filters, URL_PLATFORM_ADMIN_AI_USAGE_TENANTS);
      const data = await fetchPlatformAiUsageTenants(endpoint);
      setTenantsPayload(data);
      setPagination(getPaginationObj(data?.tenants, filters));
    } catch (err) {
      setTenantsPayload(null);
      setError(err?.message || "Failed to load tenant leaderboard");
    } finally {
      setTenantsLoading(false);
    }
  }, [canViewAiUsage, filters]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search === searchInput) {
          return prev;
        }

        return { ...prev, search: searchInput, page: 1 };
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const rows = useMemo(() => {
    const tenants = tenantsPayload?.tenants;
    return Array.isArray(tenants?.data) ? tenants.data : [];
  }, [tenantsPayload]);

  const handlePresetChange = (presetId) => {
    setFilters((prev) => createAiUsageFiltersForPreset(presetId, { ...prev, page: 1 }));
  };

  const handleCareSectorChange = (event) => {
    setFilters((prev) => ({
      ...prev,
      careSector: event.target.value,
      page: 1,
    }));
  };

  const handleGranularityChange = (nextGranularity) => {
    setFilters((prev) => ({
      ...prev,
      granularity: nextGranularity,
    }));
  };

  const handleSortChange = (sort, direction) => {
    setFilters((prev) => ({
      ...prev,
      ...normalizeTenantSort(sort, direction),
      page: 1,
    }));
  };

  const handleExport = async () => {
    setExporting(true);
    setError(null);

    try {
      await exportPlatformAiUsageCsv(filters);
    } catch (err) {
      setError(err?.message || "Failed to export AI usage CSV");
    } finally {
      setExporting(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);

    try {
      const result = await refreshPlatformAiUsage();
      toast.success(`Refreshed ${result?.refreshed ?? 0} tenant snapshots`);
      await Promise.all([loadOverview(), loadTenants()]);
    } catch (err) {
      const message = err?.message || "Failed to refresh AI usage snapshots";
      toast.error(message);
      setError(message);
    } finally {
      setRefreshing(false);
    }
  };

  if (!canViewAiUsage) {
    return (
      <PaAccessDenied
        title="AI usage"
        subtitle="Cross-tenant AI credit spend"
        description="You need platform.ai_usage.view."
      />
    );
  }

  const initialLoading = overviewLoading && !overview;

  return (
    <PlatformAdminLayout>
      <PaPageHeader
        title="AI usage"
        subtitle={formatAiUsageMeta(overview?.meta)}
        actions={
          canExportAiUsage || canRefreshAiUsage ? (
            <div className="flex flex-wrap gap-2">
              {canRefreshAiUsage ? (
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={refreshing || overviewLoading}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {refreshing ? "Refreshing…" : "Refresh snapshots"}
                </button>
              ) : null}
              {canExportAiUsage ? (
                <button
                  type="button"
                  onClick={handleExport}
                  disabled={exporting || overviewLoading}
                  className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {exporting ? "Exporting…" : "Export CSV"}
                </button>
              ) : null}
            </div>
          ) : null
        }
      />

      <AiUsageFilterBar
        filters={filters}
        granularity={granularity}
        granularityOptions={granularityOptions}
        onPresetChange={handlePresetChange}
        onCareSectorChange={handleCareSectorChange}
        onGranularityChange={handleGranularityChange}
        disabled={overviewLoading && !overview}
      />

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {overview?.alerts?.length ? (
        <div className="mb-4 space-y-2">
          {overview.alerts.map((alert) => (
            <div
              key={`${alert.type}-${alert.sp_id}`}
              className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            >
              {formatAlertSummary(alert)}
            </div>
          ))}
        </div>
      ) : null}

      {initialLoading ? (
        <AiUsageOverviewSkeleton />
      ) : (
        <>
          <AiUsageKpiStrip totals={overview?.totals} comparison={overview?.comparison} />
          <AiUsageInsightsStrip totals={overview?.totals} />

          <div className="mb-6 grid items-start gap-5 xl:grid-cols-2">
            <PlatformAiCostOverTimeChart
              series={overview?.series}
              from={dateRange.from}
              to={dateRange.to}
              loading={overviewLoading}
              granularity={granularity}
            />
            <PlatformAiUsageByFeatureChart
              items={overview?.by_feature}
              trends={overview?.feature_trends}
              from={dateRange.from}
              to={dateRange.to}
              loading={overviewLoading}
              granularity={granularity}
            />
          </div>

          <div className="mb-6 grid items-start gap-5 xl:grid-cols-2">
            <PlatformAiUsageByModelChart
              items={overview?.by_model}
              trends={overview?.model_trends}
              from={dateRange.from}
              to={dateRange.to}
              loading={overviewLoading}
              granularity={granularity}
            />
            <PlatformAiUsageByStatusChart
              items={overview?.by_status}
              trends={overview?.status_trends}
              from={dateRange.from}
              to={dateRange.to}
              loading={overviewLoading}
              granularity={granularity}
            />
          </div>

          {!filters.careSector && (overview?.by_sector?.length || overview?.sector_trends?.length) ? (
            <div className="mb-6">
              <PlatformAiUsageBySectorChart
                items={overview?.by_sector}
                trends={overview?.sector_trends}
                from={dateRange.from}
                to={dateRange.to}
                loading={overviewLoading}
                granularity={granularity}
              />
            </div>
          ) : null}

          <PaFilterBar
            searchValue={searchInput}
            onSearchChange={setSearchInput}
            searchPlaceholder="Search tenant"
            onClear={
              searchInput
                ? () => {
                    setSearchInput("");
                    setFilters((prev) => ({ ...DEFAULT_AI_USAGE_FILTERS, careSector: prev.careSector }));
                  }
                : undefined
            }
            resultCount={pagination.total}
          />

          <AiUsageTenantTable
            rows={rows}
            loading={tenantsLoading}
            sort={filters.sort}
            direction={filters.direction}
            onSortChange={handleSortChange}
            page={filters.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            perPage={filters.perPage}
            onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
          />
        </>
      )}
    </PlatformAdminLayout>
  );
};

export default AiUsageOverview;
